import { CashierPath } from '@ps/web-pam-types';
import { deflate } from 'pako';
import { useRef } from 'react';
import type { Plaid } from 'react-plaid-link';
import { useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';

import type { RedirectContext } from '../../../../../../../types/Cashier';

import type {
  AndroidPayload,
  IOSPayload,
  NormalizedTransactionResult,
  PlaidMobileEmbeddedEvent,
  PlaidWebEvent
} from './useOpenBanking.d';

const PLAID_SCRIPT_SRC = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

const useOpenBanking = (context: RedirectContext) => {
  const hasOpenedLink = useRef<Record<string, boolean>>({});
  const sessionParams = useSelector(select.location.getQuery);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isAndroid = useSelector(select.device.isAndroid);
  const request = useRequest();

  const getNormalizedIOSPayload = ({
    publicToken,
    metadata
  }: IOSPayload): NormalizedTransactionResult => ({
    type: context.deposit?.type ?? -1,
    transactionId: context.transactionId,
    payload: {
      publicToken,
      linkToken: context.plaidLinkToken ?? '',
      institutionId: metadata.institution.id,
      institutionName: metadata.institution.name,
      linkSessionId: metadata.linkSessionID
    }
  });

  const getNormalizedAndroidPayload = (payload: AndroidPayload): NormalizedTransactionResult => ({
    type: context.deposit?.type ?? -1,
    transactionId: context.transactionId,
    payload: {
      publicToken: payload.public_token,
      linkToken: context.plaidLinkToken ?? '',
      institutionId: payload.institution.institution_id,
      institutionName: payload.institution.name,
      linkSessionId: payload.link_session_id,
      transferStatus: payload.transfer_status
    }
  });

  const getNormalizedWebPayload = ({
    publicToken,
    metadata
  }: PlaidWebEvent): NormalizedTransactionResult => ({
    type: context.deposit?.type ?? -1,
    transactionId: context.transactionId,
    payload: {
      publicToken,
      linkToken: context.plaidLinkToken ?? '',
      institutionId: metadata.institution?.institution_id ?? '',
      institutionName: metadata.institution?.name ?? '',
      linkSessionId: metadata.link_session_id,
      transferStatus: metadata.transfer_status
    }
  });

  const getNormalizedPayload = (
    event?: PlaidMobileEmbeddedEvent | PlaidWebEvent
  ): NormalizedTransactionResult | undefined => {
    const isCompleteWebTransaction =
      !isMobileEmbedded && (event as PlaidWebEvent)?.metadata?.institution;
    const isCompleteMobileNativeTransaction =
      isMobileEmbedded && (event as PlaidMobileEmbeddedEvent)?.detail?.payload;

    if (!isCompleteMobileNativeTransaction && !isCompleteWebTransaction) {
      return;
    }

    if (isCompleteMobileNativeTransaction) {
      return isAndroid
        ? getNormalizedAndroidPayload(
            (event as PlaidMobileEmbeddedEvent).detail.payload as AndroidPayload
          )
        : getNormalizedIOSPayload((event as PlaidMobileEmbeddedEvent).detail.payload as IOSPayload);
    }

    return getNormalizedWebPayload(event as PlaidWebEvent);
  };

  const getUrlEncodedTransactionData = () =>
    encodeURIComponent(Buffer.from(deflate(JSON.stringify(context))).toString('base64'));

  const getUrlEncodedSessionInfo = () =>
    Object.keys(sessionParams)
      .map((key) => `${key}=${encodeURIComponent(sessionParams[key] ?? '')}`)
      .join('&');

  const handlePlaidClosed = async (event?: PlaidMobileEmbeddedEvent | PlaidWebEvent) => {
    context.plaidLinkToken && (hasOpenedLink.current[context.plaidLinkToken] = false);
    const normalizedPayload = getNormalizedPayload(event);

    if (normalizedPayload) {
      await lastValueFrom(
        request({
          service: 'wc3.paysystem.post.clientPaymentInfo',
          body: normalizedPayload
        })
      );
    }

    const transactionData = getUrlEncodedTransactionData();
    let redirectUrl = `${window.location.origin}${CashierPath.DepositResult}?${transactionData}`;

    if (isMobileEmbedded) {
      document.removeEventListener(
        'plaidclosed',
        handlePlaidClosed as unknown as (e: Event) => void
      );
      const sessionInfo = getUrlEncodedSessionInfo();
      redirectUrl += `&${sessionInfo}`;
    }

    window.location.assign(redirectUrl);
  };

  const openNativePlaidSdk = () => {
    document.addEventListener('plaidclosed', handlePlaidClosed as unknown as (e: Event) => void);
    window?.starsweb?.plaid?.open?.(context.plaidLinkToken as string);
  };

  const openWebPlaidLink = () => {
    const rootElement = document.getElementById('root');
    const jsPart = document.createElement('script');
    const jssrc = PLAID_SCRIPT_SRC;

    jsPart.type = 'text/javascript';
    jsPart.async = true;
    jsPart.src = jssrc;

    rootElement?.append(jsPart);

    jsPart.onload = () => {
      const handler = (window.Plaid as unknown as Plaid).create({
        token: context.plaidLinkToken as string,
        onSuccess: (publicToken, metadata) => handlePlaidClosed({ publicToken, metadata }),
        onExit: () => handlePlaidClosed()
      });
      handler.open();
    };
  };

  const openLink = () => {
    if (context.plaidLinkToken && hasOpenedLink.current[context.plaidLinkToken]) {
      return;
    }
    context.plaidLinkToken && (hasOpenedLink.current[context.plaidLinkToken] = true);

    if (isMobileEmbedded) {
      openNativePlaidSdk();
      return;
    }

    openWebPlaidLink();
  };

  return { openLink };
};

export default useOpenBanking;
